import { useMutation } from '@tanstack/react-query';
import { maskPhone } from '@war-game/contracts';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import { api, errorMessage } from '../api/client';
import { Alert, Button, Field } from '../components/ui';
import { phoneInputMask } from '../lib/phone';
import { useAuth } from '../store/auth';

function AuthFrame({ title, children }: { title: string; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-ink-900 px-4 py-10">
      <div className="mb-2 w-full max-w-sm bg-secret py-0.5 text-center font-mono text-[10px] font-bold tracking-[0.18em] text-white">
        OSTENSIVO // ACESSO AO SISTEMA C2
      </div>
      <div className="w-full max-w-sm border border-line bg-panel">
        <div className="border-b border-line px-5 py-4">
          <div className="font-mono text-[15px] font-bold tracking-[0.12em] text-amber">
            {t('app.name').toUpperCase()}
          </div>
          <div className="font-mono text-[10px] tracking-[0.1em] text-dim uppercase">{t('app.tagline')}</div>
        </div>
        <div className="flex flex-col gap-4 px-5 py-5">
          <h1 className="font-mono text-[12px] font-bold tracking-[0.1em] uppercase">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuth((s) => s.setSession);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const m = useMutation({
    mutationFn: () => api.call('login', { body: { login, password } }),
    onSuccess: (res) => {
      setSession(res.accessToken, res.user);
      navigate((location.state as { from?: string } | null)?.from ?? '/', { replace: true });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    m.mutate();
  };
  return (
    <AuthFrame title={t('auth.login')}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Field label={t('auth.loginField')}>
          <input
            className="c2-input"
            autoComplete="username"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            required
          />
        </Field>
        <Field label={t('auth.password')}>
          <input
            className="c2-input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Field>
        {m.error && <Alert>{errorMessage(m.error)}</Alert>}
        <Button variant="primary" type="submit" disabled={m.isPending} className="h-10">
          {t('auth.login')}
        </Button>
      </form>
      <div className="flex justify-between font-mono text-[11px]">
        <Link to="/esqueci-senha">{t('auth.forgot')}</Link>
        <Link to="/cadastro">{t('auth.register')}</Link>
      </div>
    </AuthFrame>
  );
}

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const [form, setForm] = useState({ phone: '', username: '', password: '' });
  const m = useMutation({
    mutationFn: () => api.call('register', { body: form }),
    onSuccess: (res) => {
      setSession(res.accessToken, res.user);
      navigate('/confirmar-celular', { replace: true, state: { sent: true } });
    },
  });
  return (
    <AuthFrame title={t('auth.register')}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
        className="flex flex-col gap-3"
      >
        <Field label={t('auth.username')} hint="3 a 20 caracteres: a-z, 0-9, _">
          <input
            className="c2-input"
            autoComplete="username"
            pattern="[A-Za-z0-9_]{3,20}"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            required
          />
        </Field>
        <Field label={t('auth.phone')} hint={t('auth.phoneHint')}>
          <PhoneInput value={form.phone} onChange={(phone) => setForm({ ...form, phone })} />
        </Field>
        <Field label={t('auth.password')} hint="Mínimo de 8 caracteres">
          <input
            className="c2-input"
            type="password"
            autoComplete="new-password"
            minLength={8}
            maxLength={128}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
          />
        </Field>
        {m.error && <Alert>{errorMessage(m.error)}</Alert>}
        <Button variant="primary" type="submit" disabled={m.isPending} className="h-10">
          {t('auth.register')}
        </Button>
      </form>
      <div className="font-mono text-[11px]">
        {t('auth.hasAccount')} <Link to="/entrar">{t('auth.login')}</Link>
      </div>
    </AuthFrame>
  );
}

/** Intervalo mínimo entre SMS para o mesmo número (o servidor também confere). */
const RESEND_S = 60;

function PhoneInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <input
      className="c2-input"
      type="tel"
      inputMode="tel"
      autoComplete="tel-national"
      placeholder="(11) 99999-0000"
      value={value}
      onChange={(e) => onChange(phoneInputMask(e.target.value))}
      required
    />
  );
}

function CodeInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <input
      className="c2-input text-center tracking-[0.4em]"
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]{6}"
      maxLength={6}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
      required
    />
  );
}

/** Contagem regressiva (s) para liberar o reenvio do código. */
function useCountdown(initial: number): [number, (s: number) => void] {
  const [left, setLeft] = useState(initial);
  useEffect(() => {
    if (left <= 0) return;
    const id = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [left]);
  return [left, setLeft];
}

/** Confirmação do celular por código: cadastro, conta antiga sem celular e troca de número. */
export function VerifyPhonePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user, setSession } = useAuth();
  const nav = location.state as { sent?: boolean; change?: boolean } | null;
  const [editing, setEditing] = useState(!user?.pendingPhone || !!nav?.change);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [wait, setWait] = useCountdown(nav?.sent ? RESEND_S : 0);
  const change = useMutation({
    mutationFn: () => api.call('changePhone', { body: { phone } }),
    onSuccess: (updated) => {
      setSession(token ?? '', updated);
      setEditing(false);
      setCode('');
      setWait(RESEND_S);
    },
  });
  const resend = useMutation({
    mutationFn: () => api.call('resendCode'),
    onSuccess: () => setWait(RESEND_S),
  });
  const verify = useMutation({
    mutationFn: () => api.call('verifyPhone', { body: { code } }),
    onSuccess: (res) => {
      setSession(res.accessToken, res.user);
      navigate('/', { replace: true });
    },
  });
  const error = verify.error ?? resend.error;
  return (
    <AuthFrame title={t('auth.verifyTitle')}>
      {editing ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            change.mutate();
          }}
          className="flex flex-col gap-3"
        >
          <Field label={t('auth.phone')} hint={t('auth.phoneHint')}>
            <PhoneInput value={phone} onChange={setPhone} />
          </Field>
          {change.error && <Alert>{errorMessage(change.error)}</Alert>}
          <Button variant="primary" type="submit" disabled={change.isPending} className="h-10">
            {t('auth.sendCode')}
          </Button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            verify.mutate();
          }}
          className="flex flex-col gap-3"
        >
          <div className="font-mono text-[11px] text-muted">
            {t('auth.codeSent', { phone: maskPhone(user?.pendingPhone ?? '') })}
          </div>
          <Field label={t('auth.code')} hint={t('auth.codeHint')}>
            <CodeInput value={code} onChange={setCode} />
          </Field>
          {error && <Alert>{errorMessage(error)}</Alert>}
          <Button
            variant="primary"
            type="submit"
            disabled={verify.isPending || code.length !== 6}
            className="h-10"
          >
            {t('auth.confirm')}
          </Button>
          <div className="flex justify-between font-mono text-[11px]">
            <button
              type="button"
              className="underline disabled:text-dim disabled:no-underline"
              disabled={wait > 0 || resend.isPending}
              onClick={() => resend.mutate()}
            >
              {wait > 0 ? t('auth.resendIn', { s: wait }) : t('auth.resend')}
            </button>
            <button
              type="button"
              className="underline"
              onClick={() => {
                setPhone('');
                setEditing(true);
              }}
            >
              {t('auth.changePhone')}
            </button>
          </div>
        </form>
      )}
      <Link to="/" className="font-mono text-[11px]">
        ◂ {t('lobby.title')}
      </Link>
    </AuthFrame>
  );
}

/** Recuperar senha em dois passos: celular → código + nova senha. */
export function ForgotPage() {
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const send = useMutation({ mutationFn: () => api.call('forgotPassword', { body: { phone } }) });
  const reset = useMutation({
    mutationFn: () => api.call('resetPassword', { body: { phone, code, password } }),
  });
  return (
    <AuthFrame title={t('auth.forgotTitle')}>
      {reset.isSuccess ? (
        <>
          <Alert tone="neutral">{t('auth.resetOk')}</Alert>
          <Link to="/entrar" className="font-mono text-[11px]">
            {t('auth.login')} ▸
          </Link>
        </>
      ) : !send.isSuccess ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send.mutate();
          }}
          className="flex flex-col gap-3"
        >
          <Field label={t('auth.phone')}>
            <PhoneInput value={phone} onChange={setPhone} />
          </Field>
          {send.error && <Alert>{errorMessage(send.error)}</Alert>}
          <Button variant="primary" type="submit" disabled={send.isPending}>
            {t('auth.sendCode')}
          </Button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            reset.mutate();
          }}
          className="flex flex-col gap-3"
        >
          <Alert tone="neutral">{t('auth.forgotSent')}</Alert>
          <Field label={t('auth.code')} hint={t('auth.codeHint')}>
            <CodeInput value={code} onChange={setCode} />
          </Field>
          <Field label={t('auth.newPassword')} hint="Mínimo de 8 caracteres">
            <input
              className="c2-input"
              type="password"
              autoComplete="new-password"
              minLength={8}
              maxLength={128}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Field>
          {reset.error && <Alert>{errorMessage(reset.error)}</Alert>}
          <Button variant="primary" type="submit" disabled={reset.isPending || code.length !== 6}>
            {t('auth.save')}
          </Button>
          <button
            type="button"
            className="text-left font-mono text-[11px] underline"
            onClick={() => send.reset()}
          >
            {t('auth.changePhone')}
          </button>
        </form>
      )}
      <Link to="/entrar" className="font-mono text-[11px]">
        ◂ {t('auth.login')}
      </Link>
    </AuthFrame>
  );
}
